import { DateTime } from 'luxon';
import * as R from 'ramda';
import React, { useCallback, useMemo, useState, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import RotateAnimation from 'components/lib/animations/RotateAnimation';
import FlexContainer from 'components/lib/ui/FlexContainer';
import Icon from 'components/lib/ui/Icon';
import PageWithNoAccountsEmptyState from 'components/lib/ui/PageWithNoAccountsEmptyState';
import MerchantSearchFlow from 'components/merchants/MerchantSearchFlow';
import AddRecurringModalCard from 'components/recurring/AddRecurringModalCard';
import RecurringHeaderControls from 'components/recurring/RecurringHeaderControls';
import RecurringHeaderTabs from 'components/recurring/RecurringHeaderTabs';
import ReviewStreamModalCard from 'components/recurring/ReviewStreamModalCard';
import RecurringAll from 'components/routes/recurring/RecurringAll';
import RecurringUpcoming from 'components/routes/recurring/RecurringUpcoming';
import SpinwheelConnectionFlow from 'components/spinwheel/SpinwheelConnectionFlow';

import useRecurringSearch from 'common/lib/hooks/recurring/useRecurringSearch';
import { useRecurringStreams } from 'common/lib/hooks/recurring/useRecurringStreams';
import useSpinwheel from 'common/lib/hooks/recurring/useSpinwheel';
import useProfileFlag from 'common/lib/hooks/users/useProfileFlag';
import { getSearchDisabledTooltipText } from 'common/lib/recurring';
import { BILL_TRACKING_EDUCATION_QUERY_PARAM } from 'common/lib/recurring/constants';
import { useRecurringActions } from 'lib/hooks/recurring/useRecurringActions';
import useModal from 'lib/hooks/useModal';
import {
  useQueryParam,
  useUpdatableQueryParam,
  useUpdateQueryParam,
} from 'lib/hooks/useQueryParams';
import { useWhatsNew } from 'lib/hooks/useWhatsNew';
import toast, { dismissToast } from 'lib/ui/toast';
import { setRecurringFilters } from 'state/recurring/actions';
import { getFilters } from 'state/recurring/selectors';

import { RECURRING } from 'common/constants/copy';
import { RECURRING_SEARCH_TOAST_TIMEOUT_MS } from 'common/constants/recurringTransactions';
import routes from 'constants/routes';

const LazyLoadedRecurringWalkthrough = React.lazy(
  () => import('components/recurring/RecurringWalkthrough'),
);

const DEFAULT_ROUTE = routes.recurring.upcoming.path;

const Recurring = () => {
  const dispatch = useDispatch();
  const filters = useSelector(getFilters);
  const { push, location } = useHistory();
  const billsEducationQueryParamPresent =
    useQueryParam(BILL_TRACKING_EDUCATION_QUERY_PARAM) === 'true';
  const reviewOpen = useQueryParam('reviewOpen') === 'true';
  const setReviewOpen = useUpdateQueryParam('reviewOpen');

  useRecurringActions();

  const updateFilters = useCallback(
    (filters: any) => {
      if (filters?.accounts?.length === 0) {
        filters = R.omit(['accounts'], filters);
      }

      dispatch(setRecurringFilters(filters));
    },
    [dispatch],
  );
  const [AddRecurringModal, { open: openAddRecurringModal, close: closeAddRecurringModal }] =
    useModal();

  const [ReviewStreamModal, { open: openReviewStreamModal, close: closeReviewStreamModal }] =
    useModal(reviewOpen);

  const [AddManualRecurringModal, { open: openAddManualRecurringModal }] = useModal();
  const [ConnectionModal, { open: openConnectionModal, close: closeConnectionModal }] = useModal();

  const [showedBillTrackingWalkthroughModal, setShowedBillTrackingWalkthroughModal] =
    useState(false);

  const { pendingStreams, refetchHouseholdHasRecurring } = useRecurringStreams();

  const [startDate, setStartDate] = useUpdatableQueryParam('startDate');
  const currentStartDate = useMemo(
    () => (startDate ? DateTime.fromISO(startDate) : DateTime.local().startOf('month')),
    [startDate],
  );
  const endDate = useMemo(() => currentStartDate.endOf('month'), [currentStartDate]);

  const { syncCreditReport, isSyncingCreditReport, resetTimeout, connectionStatus } =
    useSpinwheel();

  const hasAlreadySyncedWithSpinwheel = connectionStatus.isConnected;
  const shouldSyncBeforeProceeding = connectionStatus.isDisconnected;

  const handleCloseConnectionModal = () => {
    resetTimeout();
    closeConnectionModal();
  };

  const onClickAddManual = useCallback(() => {
    closeAddRecurringModal();
    openAddManualRecurringModal();
  }, [closeAddRecurringModal, openAddManualRecurringModal]);

  const onClickAddBills = useCallback(() => {
    closeAddRecurringModal();
    if (shouldSyncBeforeProceeding) {
      syncCreditReport();
      openConnectionModal();
    } else {
      push(routes.recurring.mapLiabilities());
    }
  }, [closeAddRecurringModal, shouldSyncBeforeProceeding, syncCreditReport, openConnectionModal]);

  const refetchAll = () => refetchHouseholdHasRecurring();

  const [recurringSearchToastId, setRecurringSearchToastId] = useState<string | undefined>();
  const [
    startRecurringSearch,
    {
      currentSearch: { searchStartedAt, searchNextAt },
    },
  ] = useRecurringSearch({
    onDidStartSearch: () => {
      closeAddRecurringModal();
      const toastId = toast({
        title: RECURRING.SEARCH.TOAST_TITLE,
        description: RECURRING.SEARCH.TOAST_SUBTITLE,
        duration: RECURRING_SEARCH_TOAST_TIMEOUT_MS,
        accessory: (
          <RotateAnimation>
            <Icon name="refresh" />
          </RotateAnimation>
        ),
      });
      setRecurringSearchToastId(toastId);
    },
    onDidFinishSearch: () => {
      dismissToast(recurringSearchToastId);
      refetchAll();
    },
    onTimeoutReached: () => {
      dismissToast(recurringSearchToastId);
      refetchAll();
    },
  });

  const searchDisabledTooltip = useMemo(
    () => getSearchDisabledTooltipText(searchStartedAt, searchNextAt),
    [searchStartedAt, searchNextAt],
  );

  // Profile flag for the recurring walkthrough
  const [dismissedWalkthroughAt, setDismissedWalkthroughAt, isLoadingDismissedWalkthroughAt] =
    useProfileFlag('dismissedRecurringWalkthroughAt');

  const { dismissWhatsNew, whatsNew } = useWhatsNew();

  const showBillTrackingWalkthroughModal =
    (!isLoadingDismissedWalkthroughAt && !dismissedWalkthroughAt) ||
    (billsEducationQueryParamPresent && !showedBillTrackingWalkthroughModal);

  useEffect(() => {
    // If whats new is bill tracking, but the user has dismissed the walkthrough, we want to dismiss whats new as well
    if (dismissedWalkthroughAt && whatsNew?.feature === 'bill-tracking') {
      dismissWhatsNew();
    }
  }, [dismissedWalkthroughAt, whatsNew, dismissWhatsNew]);

  return (
    <PageWithNoAccountsEmptyState
      name="Recurring"
      tabs={<RecurringHeaderTabs />}
      emptyIcon="calendar"
      emptyTitle={RECURRING.EMPTY_STATE_TITLE}
      emptyButtonText={RECURRING.EMPTY_ACCOUNTS_BUTTON_TEXT}
      controls={
        <RecurringHeaderControls
          // @ts-ignore mismatch between graphql type and redux type
          filters={filters}
          onChangeFilters={updateFilters}
          onClickAddRecurring={openAddRecurringModal}
        />
      }
    >
      <FlexContainer alignStart>
        <Switch>
          {/* This redirects the user to the default route if they try to access the page by
            the direct URL (/recurring). Without this we'd render a blank page. */}
          <Route
            exact
            path={routes.recurring.path}
            render={() => <Redirect to={{ pathname: DEFAULT_ROUTE, search: location.search }} />}
          />
          <Route path={routes.recurring.upcoming.path}>
            <RecurringUpcoming
              currentStartDate={currentStartDate}
              endDate={endDate}
              filters={filters}
              setStartDate={setStartDate}
              streamsToReview={pendingStreams}
              onClickToReviewStreams={openReviewStreamModal}
              openAddRecurringModal={openAddRecurringModal}
              openAddManualRecurringModal={openAddManualRecurringModal}
              onClickAddBills={onClickAddBills}
            />
          </Route>
          <Route path={routes.recurring.all.path}>
            <RecurringAll filters={filters} startDate={currentStartDate} />
          </Route>
        </Switch>
      </FlexContainer>

      <AddRecurringModal full>
        <AddRecurringModalCard
          searchDisabledTooltip={searchDisabledTooltip}
          onClickSearch={startRecurringSearch}
          onClickAddManual={onClickAddManual}
          onClickAddBills={onClickAddBills}
          hasAlreadySyncedWithSpinwheel={hasAlreadySyncedWithSpinwheel}
        />
      </AddRecurringModal>

      <ReviewStreamModal onClose={() => setReviewOpen(undefined)}>
        <ReviewStreamModalCard streamsToReview={pendingStreams} close={closeReviewStreamModal} />
      </ReviewStreamModal>

      <AddManualRecurringModal>
        <MerchantSearchFlow onComplete={refetchAll} initialValues={{ isRecurring: true }} />
      </AddManualRecurringModal>

      <ConnectionModal onClose={handleCloseConnectionModal}>
        <SpinwheelConnectionFlow
          isLoading={isSyncingCreditReport}
          hasFraudError={connectionStatus.hasFraudError}
          hasGenericError={connectionStatus.hasGenericError}
          onComplete={handleCloseConnectionModal}
        />
      </ConnectionModal>

      {showBillTrackingWalkthroughModal && (
        <Suspense fallback={null}>
          <LazyLoadedRecurringWalkthrough
            onFinish={(wasSkipped) => {
              if (!wasSkipped) {
                push(routes.recurring.mapLiabilities());
              }
              setDismissedWalkthroughAt(new Date().toISOString());
              setShowedBillTrackingWalkthroughModal(true);
            }}
            onStart={() => {
              dismissWhatsNew();
            }}
          />
        </Suspense>
      )}
    </PageWithNoAccountsEmptyState>
  );
};

export default Recurring;
